import { ActionCreator, AnyAction } from 'redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { ApplicationState } from '../rootReducer';
import {
  setCurrentConfiguration,
  setExpandedConfiguration,
  setIsBulkDeleting,
  setIsCreatingConfiguration,
  setIsLoadingConfigurations,
  setIsRecentlySaved,
  updateConfigurationsList,
} from './configurationsReducer';
import { BlazeMeterApiResponse } from '../../types/rest/blazeMeterApiResponse';
import acApi from '../../axios/ac/acApi';
import { Configuration } from '../../types/rest/configurationsType';
import { showToastNotification } from '../ui/toasts/toastsActions';
import { ToastType } from '../ui/toasts/toastsTypes';
import i18n from '../../i18n';
import MixpanelService from '../../utils/mixpanelService';
import { CLONE_CONFIGURATION } from '../../utils/mixpanelEvents';
import { generateDate } from '../../utils/dateUtil';
import _ from 'lodash';

/**
 * Gets List of Configurations
 */
export const getConfigurationsListAction: ActionCreator<any> = (): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    try {
      dispatch(setIsLoadingConfigurations(true));
      const response: BlazeMeterApiResponse = (await acApi.configurations.getConfigurations()).data;
      dispatch(setIsLoadingConfigurations(false));
      dispatch(updateConfigurationsList(response.result));
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, error.response.data.error));
    }
  };
};

/**
 * Create (or Clone) or Patch Configuration
 */
export const createOrUpdateConfiguration: ActionCreator<any> = (
  configuration: Configuration,
  clone?: boolean,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, any>) => {
    try {
      let response = {} as BlazeMeterApiResponse;
      let successMessage =
        clone === true ? 'configurations:actions.successCloneConfig' : 'configurations:actions.successSave';
      if (configuration.id === -1) {
        response = (await acApi.configurations.postConfiguration(configuration)).data;
      } else {
        successMessage = 'configurations:actions.successUpdate';
        response = (await acApi.configurations.putConfiguration(configuration)).data;
      }
      const config = response.result;
      dispatch(showToastNotification(ToastType.Success, `${i18n.t(successMessage)} ${config.name}`));
      dispatch(setCurrentConfiguration(config));
      dispatch(getConfigurationsListAction());
      if (configuration.id === -1) {
        dispatch(setIsCreatingConfiguration(false));
      }
      dispatch(setExpandedConfiguration(config.id));
      dispatch(setIsRecentlySaved(true));
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, error.response.data.error));
    }
  };
};

export const deleteConfiguration: ActionCreator<any> = (
  id: number,
  force: boolean,
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, any>, getState) => {
    try {
      await acApi.configurations.deleteConfiguration(id, force);
      dispatch(getConfigurationsListAction());

      //If current id was set as current, clear it out
      const current: Configuration = getState().configurations.currentConfiguration;
      if (!_.isNil(current) && current.id === id) {
        dispatch(setExpandedConfiguration(null));
        dispatch(setCurrentConfiguration(null));
      }
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, error.response.data.error));
    }
  };
};

export const cloneConfiguration = (
  configuration: Configuration,
): ThunkAction<any, ApplicationState, null, AnyAction> => {
  return async (dispatch: ThunkDispatch<ApplicationState, null, AnyAction>) => {
    MixpanelService.track(CLONE_CONFIGURATION());
    const configurationToClone = { ...configuration };
    configurationToClone.id = -1;
    configurationToClone.name = i18n.t('transactions:actions.clonedPostfix', {
      name: configurationToClone.name,
      date: generateDate(),
    });
    dispatch(createOrUpdateConfiguration(configurationToClone, true));
  };
};

/**
 * Bulk delete a list of configurations.
 * Shows a "Please wait" loading overlay while the operation is in progress.
 * On partial failure, shows a danger toast summarising the errors.
 * On full success, shows a success toast.
 */
export const bulkDeleteConfigurationsAction: ActionCreator<any> = (
  configurations: Configuration[],
): ThunkAction<any, ApplicationState, null, any> => {
  return async (dispatch: ThunkDispatch<any, any, any>, getState) => {
    const ids = configurations.map(c => c.id);
    const count = ids.length;
    dispatch(setIsBulkDeleting(true));
    try {
      const response = (await acApi.configurations.bulkDeleteConfigurations(ids)).data;
      const errors: any[] = response.result || [];
      if (errors.length > 0) {
        dispatch(
          showToastNotification(
            ToastType.Danger,
            i18n.t('configurations:configurationsTable.bulkDelete.errorTitle'),
            errors.map((e: any) => e.message).join(', '),
          ),
        );
      } else {
        dispatch(
          showToastNotification(
            ToastType.Success,
            count === 1
              ? i18n.t('configurations:configurationsTable.bulkDelete.successSingle', { count })
              : i18n.t('configurations:configurationsTable.bulkDelete.successMultiple', { count }),
          ),
        );
      }
      dispatch(getConfigurationsListAction());
      // Clear expanded/editing state if the currently-open configuration was among those deleted
      const current: Configuration = getState().configurations.currentConfiguration;
      if (!_.isNil(current) && ids.includes(current.id)) {
        dispatch(setExpandedConfiguration(null));
        dispatch(setCurrentConfiguration(null));
      }
    } catch (error) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('configurations:configurationsTable.bulkDelete.errorTitle'),
          error?.response?.data?.error || error?.message,
        ),
      );
      throw error;
    } finally {
      dispatch(setIsBulkDeleting(false));
    }
  };
};
